import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTypedOpenApi } from '../../providers';
import { favoriteKeys } from './favoriteKeys';
import { addFavorite, BOAT, type FavoritableType, type Favorite, removeFavorite } from './favoritesApi';

type MutationOptions = {
  favoritableType?: FavoritableType;
};

/**
 * The two halves of a favorite toggle.
 *
 * Both write the list cache before the request answers and roll back if it fails, rather than
 * waiting on a refetch. A heart that fills a round trip after the click reads as a broken button,
 * and this list is the caller's own data — the optimistic value is what the server will hold unless
 * the write actually fails.
 *
 * The cache is settled by invalidating on completion either way: the server owns the canonical row,
 * and favoriting something already favorited answers with a `uuid` that is not the one that was
 * sent.
 */

/** Rolls the list back to `previous`, or drops the optimistic entry if there was no cached list. */
const useOptimisticFavorites = (favoritableType: FavoritableType) => {
  const queryClient = useQueryClient();
  const queryKey = favoriteKeys.list(favoritableType);

  return {
    queryClient,
    queryKey,
    snapshot: async () => {
      // Stop an in-flight read from landing on top of the optimistic write.
      await queryClient.cancelQueries({ queryKey });

      return queryClient.getQueryData<Favorite[]>(queryKey);
    },
    rollback: (previous: Favorite[] | undefined) => {
      queryClient.setQueryData(queryKey, previous);
    },
    settle: () => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.lists() });
    },
  };
};

/**
 * Favorite something by its Sanity document id.
 *
 * The uuid is generated here rather than server-side so the optimistic row carries the same
 * identity the request does. Without that, a user who clicks twice quickly would delete a
 * placeholder uuid the server has never seen — reported as success, leaving the real favorite in
 * place.
 */
export const useAddFavoriteMutation = ({ favoritableType = BOAT }: MutationOptions = {}) => {
  const { apiClient } = useTypedOpenApi();
  const { queryClient, queryKey, snapshot, rollback, settle } = useOptimisticFavorites(favoritableType);

  return useMutation({
    mutationFn: ({ favoritableRef, uuid }: { favoritableRef: string; uuid: string }) =>
      addFavorite(apiClient, favoritableRef, favoritableType, uuid),
    onMutate: async ({ favoritableRef, uuid }) => {
      const previous = await snapshot();

      queryClient.setQueryData<Favorite[]>(queryKey, (current = []) => [
        {
          uuid,
          favoritable_type: favoritableType,
          favoritable_ref: favoritableRef,
          created_at: new Date().toISOString(),
        },
        ...current,
      ]);

      return { previous };
    },
    onError: (_error, _variables, context) => rollback(context?.previous),
    onSettled: settle,
  });
};

/** Remove a favorite by its own uuid — `favoriteUuidOf` on the list query is where that comes from. */
export const useRemoveFavoriteMutation = ({ favoritableType = BOAT }: MutationOptions = {}) => {
  const { apiClient } = useTypedOpenApi();
  const { queryClient, queryKey, snapshot, rollback, settle } = useOptimisticFavorites(favoritableType);

  return useMutation({
    mutationFn: ({ uuid }: { uuid: string }) => removeFavorite(apiClient, uuid),
    onMutate: async ({ uuid }) => {
      const previous = await snapshot();

      queryClient.setQueryData<Favorite[]>(queryKey, (current = []) =>
        current.filter((favorite) => favorite.uuid !== uuid),
      );

      return { previous };
    },
    onError: (_error, _variables, context) => rollback(context?.previous),
    onSettled: settle,
  });
};
